/**
 * The requestAnimationFrame loop with a safety net. The next frame is booked
 * before this one runs, so an exception in one frame (a renderer, HUD or audio
 * bug on some odd snapshot) costs that frame instead of stopping the loop for
 * good: before, one throw left the arena frozen on its last picture while the
 * socket carried on underneath. Each distinct error is reported once (and at
 * most MAX_REPORTED in all), so a bug that throws every frame does not flood
 * the console 60 times a second.
 */

export type RequestFrame = (cb: (now: number) => void) => unknown;

/** Distinct errors reported per page; later ones are dropped silently. */
export const MAX_REPORTED = 20;

export function runFrameLoop(raf: RequestFrame, step: (now: number) => void, report: (err: unknown) => void): void {
  const seen = new Set<string>();
  const frame = (now: number): void => {
    raf(frame);
    try {
      step(now);
    } catch (err) {
      const key = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      if (seen.size >= MAX_REPORTED || seen.has(key)) return;
      seen.add(key);
      report(err);
    }
  };
  raf(frame);
}
